import { StripeAccountResponse } from '@util/firestore/payments';

export type PayoutSection =
  | 'business_info'
  | 'ssn'
  | 'id_document'
  | 'external_account';

const SECTION_LABELS: Record<PayoutSection, string> = {
  business_info: 'Your legal name, address and date of birth',
  ssn: 'Your SSN, so Stripe can verify your identity',
  id_document: 'A photo of your government issued ID',
  external_account: 'A bank account or debit card to get paid on',
};

export function getPayoutSectionLabel(section: PayoutSection) {
  return SECTION_LABELS[section];
}

// Stripe reports the owner's fields under `individual` on individual accounts
// and under the representative Person (`person_xxx.` or `representative.`) on
// company accounts. All three mean the same thing to us.
function normalizeStripeRequirement(requirement: string) {
  return requirement
    .replace(/^person_[^.]+\./, 'individual.')
    .replace(/^representative\./, 'individual.');
}

function getDueRequirements(account?: StripeAccountResponse | null) {
  return [
    ...(account?.requirements?.currently_due ?? []),
    ...(account?.requirements?.past_due ?? []),
  ].map(normalizeStripeRequirement);
}

// The only section we cannot ask for up front: the document goes through a
// Stripe Identity session, which exists only once Stripe asks for it.
function needsIdDocument(account?: StripeAccountResponse | null) {
  return getDueRequirements(account).some(
    (requirement) =>
      requirement.endsWith('.document') || requirement.endsWith('_document')
  );
}

// The Stripe key we have to write the identity number to depends on which one
// Stripe is asking for: the full number verifies the account, the last 4 only
// confirms it.
export function getIdNumberKey(account?: StripeAccountResponse | null) {
  return getDueRequirements(account).includes('individual.id_number')
    ? 'individual.id_number'
    : 'individual.ssn_last_4';
}

// Every seller is asked for the same things in the same order, whether or not
// Stripe happens to list them yet. A brand new account keeps most fields in
// `eventually_due`, so deriving the steps from Stripe would show a bank form
// and nothing else, then fail at the first cash out.
export type PayoutBusinessType = 'individual' | 'company';

export function getPayoutSections(
  account?: StripeAccountResponse | null,
  businessType: PayoutBusinessType = 'individual'
): PayoutSection[] {
  const sections: PayoutSection[] = ['business_info'];

  // The company form already asks for the owner's last 4, so companies only get
  // their own SSN step when Stripe escalates to the full number.
  const needsFullSsn = getIdNumberKey(account) === 'individual.id_number';
  if (businessType !== 'company' || needsFullSsn) sections.push('ssn');

  if (needsIdDocument(account)) sections.push('id_document');

  sections.push('external_account');
  return sections;
}

function isSectionComplete(
  account: StripeAccountResponse,
  section: PayoutSection,
  hasShopAddress: boolean,
  businessType: PayoutBusinessType
) {
  const individual = account.individual;

  switch (section) {
    case 'business_info':
      // The address doubles as the shop's ship from location, so it has to
      // land on our user doc too, not just on Stripe.
      if (!hasShopAddress) return false;
      return businessType === 'company'
        ? !!account.company?.address?.line1 && !!account.company?.tax_id_provided
        : !!individual?.address?.line1 &&
            !!individual?.first_name &&
            !!individual?.last_name &&
            !!individual?.dob?.year;
    case 'ssn': {
      // Stripe asking again outranks any provided flag: the last 4 stays marked
      // as provided even after Stripe escalates to the full number, and a
      // company's owner is a separate Person this response does not carry.
      const stillAsking = getDueRequirements(account).some(
        (requirement) =>
          requirement === 'individual.ssn_last_4' ||
          requirement === 'individual.id_number'
      );
      if (stillAsking) return false;
      if (businessType === 'company') return true;
      return (
        !!individual?.ssn_last_4_provided || !!individual?.id_number_provided
      );
    }
    case 'id_document':
      return !needsIdDocument(account);
    case 'external_account':
      return !!account.external_accounts?.data?.length;
  }
}

export function getIncompletePayoutSections(
  account?: StripeAccountResponse | null,
  hasShopAddress = false,
  businessType: PayoutBusinessType = 'individual'
): PayoutSection[] {
  if (!account) return [];

  const incomplete = getPayoutSections(account, businessType).filter(
    (section) =>
      !isSectionComplete(account, section, hasShopAddress, businessType)
  );

  // Stripe can withhold payouts over a field none of the checks above look at.
  // Only step in when it is actually holding the money and still asking for
  // something, so a requirement sitting against a future deadline never nags a
  // seller whose payouts already work.
  if (
    !incomplete.length &&
    account.payouts_enabled === false &&
    getDueRequirements(account).length > 0
  ) {
    return ['business_info'];
  }

  return incomplete;
}

// What stops a cash out today. Stripe's own verdict rather than our reading of
// the requirement list: an entry can sit in `currently_due` against a future
// deadline while payouts keep running, so treating any of them as blocking
// would refuse cash outs Stripe is perfectly happy to make.
export function isPayoutBlocked(account?: StripeAccountResponse | null) {
  if (!account) return false;
  return (
    account.payouts_enabled === false ||
    !account.external_accounts?.data?.length
  );
}

// Stripe holds payouts while it reviews what was already submitted. Nothing is
// due from the seller in that window, so it is not the same as an unfinished
// setup and must not be reported as one.
export function isPayoutVerificationPending(
  account?: StripeAccountResponse | null
) {
  if (!account) return false;
  return (
    account.payouts_enabled === false &&
    !!account.external_accounts?.data?.length &&
    getDueRequirements(account).length === 0
  );
}
